import { useLayoutEffect, useState } from "react";

import {
  Button,
  Combobox,
  Input,
  LoadingSpinner,
  typographyStyles,
} from "@newsela/angelou";
import { flatten, isEmpty } from "@s-libs/micro-dash";
import PropTypes from "prop-types";

import globalToasterRef from "@/base/globalToasterRef";
import USER_ROLES from "@/constants/USER_ROLES";
import FIELD_DISABLED_HELPER_TEXT from "@/directory/constants/FIELD_DISABLED_HELPER_TEXT";
import useTeacherUpdate from "@/teacher/hooks/useTeacherUpdate";
import getHighestRoleLevel from "@/users/getHighestRoleLevel";
import useUserDelete from "@/users/useUserDelete";
import useUserPost from "@/users/useUserPost";
import {
  canSubmitForm,
  EVENT_TOAST_FAILED_UPDATED,
  EVENT_TOAST_SUCCESS_UPDATED,
  getInvalidFields,
  getUpdatedFields,
  INVALID_FIELD_MESSAGE,
  FORM_VALIDATION,
} from "./UserProfile.utils";
import {
  containerCss,
  saveButtonCss,
  savingLoadingCss,
  submitContainerCss,
  tooltipDialog,
} from "./UserProfile.style";

const propTypes = {
  /**
   * User information
   */
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    first_name: PropTypes.string.isRequired,
    last_name: PropTypes.string.isRequired,
    username: PropTypes.string.isRequired,
    email: PropTypes.string,
    is_active: PropTypes.bool.isRequired,
    roles: PropTypes.arrayOf(PropTypes.string.isRequired).isRequired,
    _editable: PropTypes.shape({
      first_name: PropTypes.bool,
      last_name: PropTypes.bool,
      username: PropTypes.bool,
      email: PropTypes.bool,
      is_active: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  /**
   * Teacher information (optional)
   */
  teacher: PropTypes.shape({
    id: PropTypes.number.isRequired,
    external_id: PropTypes.string,
    _editable: PropTypes.shape({
      external_id: PropTypes.bool,
    }).isRequired,
  }),
  /**
   * Function called after user updated sending new user information
   */
  onChangeUser: PropTypes.func.isRequired,
  /**
   * Function called after teacher updated sending new teacher information (optional)
   */
  onChangeTeacher: PropTypes.func,
  /**
   * Hide fields of user profile
   */
  hiddenFields: PropTypes.arrayOf(PropTypes.string),
};

const defaultProps = {
  hiddenFields: [],
};

const UserProfile = ({
  user: initialUser,
  teacher: initialTeacher,
  onChangeUser,
  onChangeTeacher,
  hiddenFields,
}) => {
  const [user, setUser] = useState(initialUser);
  const [teacher, setTeacher] = useState(initialTeacher);
  const [updateUser, { loading: loadingUser }] = useUserPost(initialUser.id);
  const [updateTeacher, { loading: loadingTeacher }] = useTeacherUpdate();
  const [deleteUser, { loading: loadingUserDelete }] = useUserDelete(
    initialUser.id,
  );
  const updatedFields = {
    user: getUpdatedFields(initialUser, user),
    teacher: getUpdatedFields(initialTeacher, teacher),
  };
  const invalidFields = {
    user: getInvalidFields(updatedFields.user, FORM_VALIDATION.user),
    teacher: getInvalidFields(updatedFields.teacher, FORM_VALIDATION.teacher),
  };
  const canSubmit = canSubmitForm(invalidFields, updatedFields);
  const isSaving = loadingUser || loadingTeacher || loadingUserDelete;

  useLayoutEffect(() => {
    setUser(initialUser);
  }, [initialUser]);

  useLayoutEffect(() => {
    setTeacher(initialTeacher);
  }, [initialTeacher]);

  const onUpdateUser = async () => {
    if (updatedFields.user.is_active === false) await deleteUser();
    return updateUser(updatedFields.user);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const [userResponse, teacherResponse] = await Promise.all([
      !isEmpty(updatedFields.user) && onUpdateUser(),
      !isEmpty(updatedFields.teacher) && updateTeacher(teacher),
    ]);
    if (userResponse.error) {
      try {
        const errorResponse = JSON.parse(userResponse.error.toJSON().message);
        const [errorMessage] = flatten(Object.values(errorResponse));
        globalToasterRef.current.displayToast({
          ...EVENT_TOAST_FAILED_UPDATED,
          children: errorMessage,
        });
      } catch {
        globalToasterRef.current.displayToast(EVENT_TOAST_FAILED_UPDATED);
      }
    } else if (teacherResponse.error) {
      globalToasterRef.current.displayToast(EVENT_TOAST_FAILED_UPDATED);
    } else {
      if (userResponse.data) onChangeUser(userResponse.data);
      if (teacherResponse.data) onChangeTeacher(teacherResponse.data);
      globalToasterRef.current.displayToast(EVENT_TOAST_SUCCESS_UPDATED);
    }
  };

  return (
    <form role="form">
      <div css={containerCss}>
        {!hiddenFields.includes("first_name") && (
          <Input
            label="First name"
            inputType={Input.inputTypes.text}
            value={user.first_name}
            onChange={(e) => setUser({ ...user, first_name: e.target.value })}
            disabled={!user._editable.first_name}
            helperText={
              user._editable.first_name ? "" : FIELD_DISABLED_HELPER_TEXT
            }
            touched
            maxLength={30}
            error={
              invalidFields.user.includes("first_name")
                ? INVALID_FIELD_MESSAGE
                : ""
            }
            __cssFor={{ label: typographyStyles.heading6Obj }}
          />
        )}
        {!hiddenFields.includes("last_name") && (
          <Input
            label="Last name"
            inputType={Input.inputTypes.text}
            value={user.last_name}
            onChange={(e) => setUser({ ...user, last_name: e.target.value })}
            disabled={!user._editable.last_name}
            helperText={
              user._editable.last_name ? "" : FIELD_DISABLED_HELPER_TEXT
            }
            touched
            maxLength={150}
            error={
              invalidFields.user.includes("last_name")
                ? INVALID_FIELD_MESSAGE
                : ""
            }
            __cssFor={{ label: typographyStyles.heading6Obj }}
          />
        )}
        {!hiddenFields.includes("username") && (
          <Input
            label="Username"
            inputType={Input.inputTypes.text}
            value={user.username}
            onChange={(e) => setUser({ ...user, username: e.target.value })}
            disabled={!user._editable.username}
            helperText={
              user._editable.username ? "" : FIELD_DISABLED_HELPER_TEXT
            }
            touched
            maxLength={75}
            error={
              invalidFields.user.includes("username")
                ? INVALID_FIELD_MESSAGE
                : ""
            }
            __cssFor={{ label: typographyStyles.heading6Obj }}
          />
        )}
        {!hiddenFields.includes("email") && (
          <Input
            label="Email address"
            isRequired={false}
            inputType={Input.inputTypes.text}
            value={user.email}
            onChange={(e) => setUser({ ...user, email: e.target.value })}
            disabled={!user._editable.email}
            helperText={user._editable.email ? "" : FIELD_DISABLED_HELPER_TEXT}
            touched
            maxLength={100}
            error={
              invalidFields.user.includes("email") ? INVALID_FIELD_MESSAGE : ""
            }
            __cssFor={{ label: typographyStyles.heading6Obj }}
          />
        )}
        {!hiddenFields.includes("is_active") && (
          <Combobox
            label="Status"
            selectedItem={user.is_active}
            itemToString={(item) => (item ? "Active" : "Inactive")}
            items={[true, false]}
            onSelectedItemChange={(e) =>
              setUser({ ...user, is_active: e.selectedItem })
            }
            createInputProps={() => ({ disabled: !user._editable.is_active })}
            touched
            toggleButtonProps={{ disabled: !user._editable.is_active }}
            helperText={
              user._editable.is_active ? "" : FIELD_DISABLED_HELPER_TEXT
            }
            error={
              invalidFields.user.includes("is_active")
                ? INVALID_FIELD_MESSAGE
                : ""
            }
          />
        )}
        {!hiddenFields.includes("roles") && (
          <Combobox
            label="Role"
            selectedItem={getHighestRoleLevel(user.roles)}
            itemToString={(item) => USER_ROLES[item] || ""}
            items={Object.keys(USER_ROLES)}
            createInputProps={() => ({ disabled: true })}
            toggleButtonProps={{ disabled: true }}
            helperText={FIELD_DISABLED_HELPER_TEXT}
            tooltipDialogProps={{
              body: "If your role is incorrect, contact your district or school administrator.",
            }}
            __cssFor={{ TooltipDialog: tooltipDialog }}
          />
        )}
        {!hiddenFields.includes("external_id") &&
          user.roles.includes("teacher") &&
          teacher && (
            <Input
              label="SIS ID"
              isRequired={false}
              maxLength={50}
              inputType={Input.inputTypes.text}
              value={teacher.external_id}
              disabled={!teacher._editable.external_id}
              helperText={
                teacher._editable.external_id ? "" : FIELD_DISABLED_HELPER_TEXT
              }
              onChange={(e) =>
                setTeacher({ ...teacher, external_id: e.target.value })
              }
              error={
                invalidFields.teacher.includes("external_id")
                  ? INVALID_FIELD_MESSAGE
                  : ""
              }
              __cssFor={{ label: typographyStyles.heading6Obj }}
            />
          )}
      </div>
      <div css={submitContainerCss}>
        <Button
          flavor={Button.flavors.primary}
          __cssFor={saveButtonCss}
          type="submit"
          disabled={!canSubmit || isSaving}
          onClick={onSubmit}
        >
          Save changes
        </Button>
        {isSaving && <LoadingSpinner __cssFor={savingLoadingCss} />}
      </div>
    </form>
  );
};

UserProfile.propTypes = propTypes;
UserProfile.defaultProps = defaultProps;

export default UserProfile;
